import { Vec2, Vec, norm, sub } from './VectorTools'

export let tup2 = (<T, U>(a: T, b: U): [T, U] => [a, b]);

export type Line = { a: number, b: number }
export type Segment = {
    line: Line,
    start: number,
    end: number,
}
export let parallelLineBy = ({ a }: Line, [x, y]: Vec2): Line => ({ a, b: y - a * x })
export let lineOfPoints = ([uX, uY]: Vec2, [vX, vY]: Vec2) => {
    let d = uX - vX;
    if (d === 0)
        d = 0.000001;
    let a = (uY - vY) / d;
    return { a, b: uY - a * uX };
};
export let segmentOfPoints = ([uX, uY]: Vec2, [vX, vY]: Vec2) => {
    let line = lineOfPoints([uX, uY], [vX, vY]);
    return { line, start: Math.min(uX, vX), end: Math.max(uX, vX) };
};
export let intersectLines = (l: Line, m: Line): number | null => {
    let d = l.a - m.a;
    return d === 0 ? null : (m.b - l.b) / d;
}
export let lineGetY = (x: number, l: Line): number => l.a * x + l.b
export let lineGetPoint = (x: number, l: Line): Vec2 => [x, lineGetY(x, l)]
export let inSegmentRange = (x: number, s: Segment) => x >= s.start && x <= s.end;
export let intersectSegments = (l: Segment, m: Segment): Vec2 | null => {
    let x = intersectLines(l.line, m.line);
    return x !== null && inSegmentRange(x, l) && inSegmentRange(x, m) ? lineGetPoint(x, l.line) : null;
}
export let pointsOfSegment = (s: Segment): [Vec2, Vec2] => (f => tup2(f(s.start), f(s.end)))((x: number): Vec2 => lineGetPoint(x, s.line))
export let rotate2dVec = ([x, y]: Vec2, angle: number): Vec2 =>
    [x * Math.cos(angle) - y * Math.sin(angle),
    x * Math.sin(angle) + y * Math.cos(angle)];

export let distancePointLine = ([x, y]: Vec2, { a, b }: Line): number =>
    Math.abs(a * x - y + b) / Math.sqrt(a ** 2 + 1);

export let perpLineBy = ([x, y]: Vec2, l: Line): Line => {
    let a = -1 / l.a;
    return { a, b: y - a * x };
};
export let projOn2DLine = (v: Vec2, l: Line): Vec2 => lineGetPoint(intersectLines(perpLineBy(v, l), l) as number, l)
export let distancePoints = <T extends Vec>(a: T, b: T): number => norm(sub(a, b))
